import { AlertTriangle, ClipboardCopy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import type { Oprava } from "@/api/hooks/useTopsolidRaw";

/**
 * „Na opravu v TopSolide“ — čo appka pri importe musela obísť.
 *
 * Sú to **chyby exportu**, nie naše rozhodnutia: rotácia ani voľba
 * delenia sem nepatria, tie sú vec appky a stroja. Sem patrí to, čo
 * treba opraviť v modeli, aby sa import nabudúce nemusel obchádzať —
 * inak príde tá istá chyba pri každom ďalšom exporte.
 *
 * Ten istý komponent je v importnom dialógu aj na zákazke; dva zoznamy
 * s tým istým obsahom by sa časom rozišli.
 */

/** Text do schránky — otvára sa vedľa TopSolidu, nie v appke. */
export function opravyAkoText(opravy: Oprava[]): string {
  return opravy
    .map((o) =>
      [o.zostava_kod, o.dielec].filter(Boolean).join(" · ") + ` — ${o.popis}`,
    )
    .join("\n");
}

async function skopiruj(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Clipboard API na http:// (dev, jsdom) nie je — starý spôsob funguje
    const pole = document.createElement("textarea");
    pole.value = text;
    document.body.appendChild(pole);
    pole.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(pole);
    return ok;
  }
}

export function OpravyZoznam({
  opravy,
  nadpis = "Na opravu v TopSolide",
}: {
  opravy: Oprava[];
  nadpis?: string;
}): JSX.Element | null {
  if (!opravy.length) return null;

  return (
    <section className="rounded-lg border border-amber-300 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/30">
      <h3 className="flex flex-wrap items-center gap-2 border-b border-amber-300 px-3 py-1.5 text-sm font-medium dark:border-amber-900">
        <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden />
        {nadpis}
        <span className="text-xs font-normal text-muted-foreground">
          ({opravy.length})
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="ml-auto h-6 text-xs"
          onClick={() =>
            void skopiruj(opravyAkoText(opravy)).then((ok) =>
              toast({
                title: ok ? "Skopírované" : "Nepodarilo sa skopírovať",
                variant: ok ? "default" : "destructive",
              }),
            )
          }
        >
          <ClipboardCopy className="mr-1 h-3.5 w-3.5" />
          Kopírovať
        </Button>
      </h3>
      <ul className="divide-y divide-amber-200 text-xs dark:divide-amber-900">
        {opravy.map((o, i) => (
          <li key={`${o.druh}-${o.zostava_kod}-${o.dielec}-${i}`} className="px-3 py-1.5">
            <span className="font-mono">{o.dielec}</span>
            {o.zostava_kod ? (
              <span className="ml-2 text-muted-foreground">{o.zostava_kod}</span>
            ) : null}
            <span className="ml-2">{o.popis}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
